import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Flex, HelpTip, Switch } from "@amzn/storm-ui";
import type { FunctionComponent } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { notificationStore, NotificationType } from "../../store/notification";
import {
    UserPreference,
    UserPreferencesStore,
} from "../../store/userPreferences";
import { logWithTakt } from "../../utils/telemetry.utils";

const Wrapper = styled(Flex)`
    margin-bottom: 15px;
`;

export const ForgeBetaOptInToggle: FunctionComponent<{
    onToggle: () => void;
}> = ({ onToggle }) => {
    const forgeViz =
        useStore(UserPreferencesStore, (state) =>
            state.getPreference(UserPreference.USE_FORGE_VISUALIZATION),
        ) === "true";

    const [setPreferences] = useStore(UserPreferencesStore, (state) => [
        state.setPreferences,
    ]);

    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );

    const _onToggle = () => {
        setPreferences({
            [UserPreference.USE_FORGE_VISUALIZATION]: (!forgeViz).toString(),
        });

        if (forgeViz) {
            const uuid = crypto.randomUUID();
            upsertNotification(
                "The insight panel will open again with non-interactive charts.",
                NotificationType.LOADING,
                uuid,
            );
            setTimeout(() => {
                dismissNotification(uuid);
            }, 4000);

            logWithTakt(TELEMETRY_ID.FORGE.BETA_OPTOUT);
            PhoenixMetrics.emit(
                TELEMETRY_ID.FORGE.BETA_OPTOUT,
                PHOENIX_FEATURE.FORGE,
            );
        } else {
            logWithTakt(TELEMETRY_ID.FORGE.BETA_OPTIN);
            PhoenixMetrics.emit(
                TELEMETRY_ID.FORGE.BETA_OPTIN,
                PHOENIX_FEATURE.FORGE,
            );
        }

        onToggle();
    };

    const helpIcon = (
        <HelpTip>
            Try insights forge for an interactive experience. Download data,
            change time ranges, translate content, and more.{" "}
            <a
                href="https://w.amazon.com/bin/view/InsightsHub/IForge/"
                target="_blank"
                rel="noopener noreferrer"
            >
                Learn more
            </a>
        </HelpTip>
    );

    return (
        <Wrapper direction="column" alignItems="flex-end">
            <Switch
                onChange={_onToggle}
                id={"toggle-forge-beta"}
                label={"Show interactive slide"}
                renderLabelEnd={helpIcon}
                toggled={forgeViz}
            />
        </Wrapper>
    );
};
